import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  SimpleGrid,
  Container,
  Heading,
  useColorModeValue,
  Spinner,
  Center,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Progress,
  Alert,
  AlertIcon,
  AlertDescription
} from '@chakra-ui/react';
import { 
  FiTrendingUp, 
  FiDollarSign, 
  FiUsers, 
  FiEye, 
  FiHeart, 
  FiMessageCircle,
  FiShare,
  FiBookmark,
  FiRefreshCw,
  FiBarChart,
  FiPieChart,
  FiClock,
  FiGlobe
} from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import AnalyticsService, { 
  type ViewAnalytics, 
  type RevenueAnalytics, 
  type DemographicAnalytics, 
  type EngagementAnalytics,
  type TrafficAnalytics,
  type CreatorDashboardAnalytics 
} from '../services/analyticsService';
import IconWrapper from './IconWrapper';

interface AnalyticsDashboardProps {
  contentId?: string;
  userId?: string;
  isCreatorDashboard?: boolean;
}

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({
  contentId,
  userId,
  isCreatorDashboard = false
}) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Analytics data
  const [viewAnalytics, setViewAnalytics] = useState<ViewAnalytics | null>(null);
  const [revenueAnalytics, setRevenueAnalytics] = useState<RevenueAnalytics | null>(null);
  const [demographicAnalytics, setDemographicAnalytics] = useState<DemographicAnalytics | null>(null);
  const [engagementAnalytics, setEngagementAnalytics] = useState<EngagementAnalytics | null>(null);
  const [trafficAnalytics, setTrafficAnalytics] = useState<TrafficAnalytics | null>(null);
  const [creatorAnalytics, setCreatorAnalytics] = useState<CreatorDashboardAnalytics | null>(null);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'white');
  const descriptionColor = useColorModeValue('gray.600', 'gray.400');

  useEffect(() => {
    loadAnalytics();
  }, [contentId, userId, isCreatorDashboard]);

  const loadAnalytics = async () => {
    if (!user) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      if (isCreatorDashboard && userId) {
        const analytics = await AnalyticsService.getCreatorDashboardAnalytics(userId);
        setCreatorAnalytics(analytics);
      } else if (contentId) {
        const [views, revenue, demographics, engagement] = await Promise.all([
          AnalyticsService.getViewAnalytics(contentId),
          AnalyticsService.getRevenueAnalytics(contentId),
          AnalyticsService.getDemographicAnalytics(contentId),
          AnalyticsService.getEngagementAnalytics(contentId)
        ]);
        
        setViewAnalytics(views);
        setRevenueAnalytics(revenue);
        setDemographicAnalytics(demographics);
        setEngagementAnalytics(engagement);
      } else {
        const traffic = await AnalyticsService.getTrafficAnalytics();
        setTrafficAnalytics(traffic);
      }
    } catch (error) {
      console.error('Error loading analytics:', error);
      setError('Failed to load analytics data');
    } finally {
      setIsLoading(false);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatPercentage = (value: number): string => {
    return `${value.toFixed(1)}%`;
  };

  if (isLoading) {
    return (
      <Center py={8}>
        <Spinner size="xl" color="purple.400" />
      </Center>
    );
  }

  if (error) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (isCreatorDashboard && creatorAnalytics) {
    return (
      <Container maxW="1200px" py={8}>
        <VStack gap={8} align="stretch">
          <HStack justify="space-between">
            <Heading size="lg" color={textColor}>Creator Dashboard</Heading>
            <Button
              leftIcon={<IconWrapper icon={FiRefreshCw} size={16} />}
              onClick={loadAnalytics}
              colorScheme="purple"
              variant="outline"
            >
              Refresh
            </Button>
          </HStack>

          {/* Overview Stats */}
          <SimpleGrid columns={{ base: 2, md: 4 }} gap={6}>
            <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
              <StatLabel>Total Views</StatLabel>
              <StatNumber color="purple.400">{formatNumber(creatorAnalytics.totalViews)}</StatNumber>
              <StatHelpText>
                <StatArrow type="increase" />
                All time
              </StatHelpText>
            </Stat>

            <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
              <StatLabel>Total Revenue</StatLabel>
              <StatNumber color="green.400">{formatCurrency(creatorAnalytics.totalRevenue)}</StatNumber>
              <StatHelpText>
                <StatArrow type="increase" />
                Lifetime earnings
              </StatHelpText>
            </Stat>

            <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
              <StatLabel>Subscribers</StatLabel>
              <StatNumber color="blue.400">{formatNumber(creatorAnalytics.totalSubscribers)}</StatNumber>
              <StatHelpText>
                <StatArrow type="increase" />
                Total followers
              </StatHelpText>
            </Stat>

            <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
              <StatLabel>Average Rating</StatLabel>
              <StatNumber color="yellow.400">{creatorAnalytics.averageRating.toFixed(1)}</StatNumber>
              <StatHelpText>
                <StatArrow type="increase" />
                Content quality
              </StatHelpText>
            </Stat>
          </SimpleGrid>

          {/* Top Performing Content */}
          <Box bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
            <Heading size="md" mb={4} color={textColor}>Top Performing Content</Heading>
            <VStack align="stretch" gap={3}>
              {creatorAnalytics.topPerformingContent.map((content, index) => (
                <HStack key={content.contentId} justify="space-between" p={3} bg="gray.50" borderRadius="md">
                  <HStack>
                    <Text fontWeight="bold" color="purple.400">#{index + 1}</Text>
                    <VStack align="start" gap={0}>
                      <Text fontWeight="medium" color={textColor}>{content.title}</Text>
                      <Text fontSize="sm" color={descriptionColor}>
                        {formatNumber(content.views)} views • {formatCurrency(content.revenue)} revenue
                      </Text>
                    </VStack>
                  </HStack>
                  <Badge colorScheme="green" variant="subtle">
                    {content.rating.toFixed(1)} ⭐
                  </Badge>
                </HStack>
              ))}
            </VStack>
          </Box>

          {/* Recent Activity */}
          <Box bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
            <Heading size="md" mb={4} color={textColor}>Recent Activity</Heading>
            <VStack align="stretch" gap={3}>
              {creatorAnalytics.recentActivity.map((activity, index) => (
                <HStack key={index} justify="space-between">
                  <HStack>
                    <IconWrapper icon={FiClock} size={16} color="gray.400" />
                    <Text color={textColor}>{activity.description}</Text>
                  </HStack>
                  <Text fontSize="sm" color={descriptionColor}>
                    {activity.timestamp.toLocaleDateString()}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </Box>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="1200px" py={8}>
      <VStack gap={8} align="stretch">
        <HStack justify="space-between">
          <Heading size="lg" color={textColor}>Analytics Dashboard</Heading>
          <Button
            leftIcon={<IconWrapper icon={FiRefreshCw} size={16} />}
            onClick={loadAnalytics}
            colorScheme="purple"
            variant="outline"
          >
            Refresh
          </Button>
        </HStack>

        <Tabs index={activeTab} onChange={setActiveTab}>
          <TabList>
            <Tab>Overview</Tab>
            <Tab>Views</Tab>
            <Tab>Revenue</Tab>
            <Tab>Demographics</Tab>
            <Tab>Engagement</Tab>
            {!contentId && <Tab>Traffic</Tab>}
          </TabList>

          <TabPanels>
            {/* Overview Tab */}
            <TabPanel>
              <SimpleGrid columns={{ base: 2, md: 4 }} gap={6}>
                {viewAnalytics && (
                  <>
                    <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <StatLabel>Total Views</StatLabel>
                      <StatNumber color="purple.400">{formatNumber(viewAnalytics.views)}</StatNumber>
                      <StatHelpText>
                        <StatArrow type="increase" />
                        {formatNumber(viewAnalytics.uniqueViews)} unique
                      </StatHelpText>
                    </Stat>

                    <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <StatLabel>Watch Time</StatLabel>
                      <StatNumber color="blue.400">{Math.round(viewAnalytics.watchTime / 60)}m</StatNumber>
                      <StatHelpText>
                        <StatArrow type="increase" />
                        Total minutes
                      </StatHelpText>
                    </Stat>

                    <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <StatLabel>Completion Rate</StatLabel>
                      <StatNumber color="green.400">{formatPercentage(viewAnalytics.completionRate)}</StatNumber>
                      <StatHelpText>
                        <StatArrow type="increase" />
                        View completion
                      </StatHelpText>
                    </Stat>
                  </>
                )}

                {revenueAnalytics && (
                  <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                    <StatLabel>Total Revenue</StatLabel>
                    <StatNumber color="green.400">{formatCurrency(revenueAnalytics.totalRevenue)}</StatNumber>
                    <StatHelpText>
                      <StatArrow type="increase" />
                      Lifetime earnings
                    </StatHelpText>
                  </Stat>
                )}
              </SimpleGrid>
            </TabPanel>

            {/* Views Tab */}
            <TabPanel>
              {viewAnalytics && (
                <VStack gap={6} align="stretch">
                  <SimpleGrid columns={{ base: 1, md: 3 }} gap={6}>
                    <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <StatLabel>Total Views</StatLabel>
                      <StatNumber color="purple.400">{formatNumber(viewAnalytics.views)}</StatNumber>
                    </Stat>

                    <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <StatLabel>Unique Views</StatLabel>
                      <StatNumber color="blue.400">{formatNumber(viewAnalytics.uniqueViews)}</StatNumber>
                    </Stat>

                    <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <StatLabel>Watch Time</StatLabel>
                      <StatNumber color="green.400">{Math.round(viewAnalytics.watchTime / 60)}m</StatNumber>
                    </Stat>
                  </SimpleGrid>

                  <Box bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                    <Heading size="md" mb={4} color={textColor}>Views Over Time</Heading>
                    <VStack align="stretch" gap={3}>
                      {viewAnalytics.viewsOverTime.slice(-7).map((data, index) => (
                        <HStack key={index} justify="space-between">
                          <Text color={textColor}>{data.date}</Text>
                          <HStack>
                            <Text color="purple.400">{data.views} views</Text>
                            <Text color="blue.400">({data.uniqueViews} unique)</Text>
                          </HStack>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                </VStack>
              )}
            </TabPanel>

            {/* Revenue Tab */}
            <TabPanel>
              {revenueAnalytics && (
                <VStack gap={6} align="stretch">
                  <SimpleGrid columns={{ base: 1, md: 4 }} gap={6}>
                    <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <StatLabel>Total Revenue</StatLabel>
                      <StatNumber color="green.400">{formatCurrency(revenueAnalytics.totalRevenue)}</StatNumber>
                    </Stat>

                    <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <StatLabel>Ad Revenue</StatLabel>
                      <StatNumber color="blue.400">{formatCurrency(revenueAnalytics.adRevenue)}</StatNumber>
                    </Stat>

                    <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <StatLabel>Donations</StatLabel>
                      <StatNumber color="purple.400">{formatCurrency(revenueAnalytics.donationRevenue)}</StatNumber>
                    </Stat>

                    <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <StatLabel>Subscriptions</StatLabel>
                      <StatNumber color="orange.400">{formatCurrency(revenueAnalytics.subscriptionRevenue)}</StatNumber>
                    </Stat>
                  </SimpleGrid>

                  <Box bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                    <Heading size="md" mb={4} color={textColor}>Revenue Over Time</Heading>
                    <VStack align="stretch" gap={3}>
                      {revenueAnalytics.revenueOverTime.slice(-7).map((data, index) => (
                        <HStack key={index} justify="space-between">
                          <Text color={textColor}>{data.date}</Text>
                          <HStack>
                            <Text color="green.400">{formatCurrency(data.revenue)}</Text>
                            <Text color="blue.400">({formatCurrency(data.adRevenue)} ads)</Text>
                          </HStack>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                </VStack>
              )}
            </TabPanel>

            {/* Demographics Tab */}
            <TabPanel>
              {demographicAnalytics && (
                <VStack gap={6} align="stretch">
                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
                    <Box bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <Heading size="md" mb={4} color={textColor}>Age Groups</Heading>
                      <VStack align="stretch" gap={3}>
                        {Object.entries(demographicAnalytics.ageGroups).map(([ageGroup, count]) => (
                          <HStack key={ageGroup} justify="space-between">
                            <Text color={textColor}>{ageGroup}</Text>
                            <HStack>
                              <Text color="purple.400">{count}</Text>
                              <Progress 
                                value={(count / Object.values(demographicAnalytics.ageGroups).reduce((a, b) => a + b, 0)) * 100} 
                                size="sm" 
                                colorScheme="purple" 
                                width="100px"
                              />
                            </HStack>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>

                    <Box bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <Heading size="md" mb={4} color={textColor}>Gender Distribution</Heading>
                      <VStack align="stretch" gap={3}>
                        {Object.entries(demographicAnalytics.genderDistribution).map(([gender, count]) => (
                          <HStack key={gender} justify="space-between">
                            <Text color={textColor} textTransform="capitalize">{gender}</Text>
                            <Text color="blue.400">{count}</Text>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  </SimpleGrid>

                  <Box bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                    <Heading size="md" mb={4} color={textColor}>Top Locations</Heading>
                    <VStack align="stretch" gap={3}>
                      {demographicAnalytics.locationDistribution.slice(0, 10).map((location, index) => (
                        <HStack key={index} justify="space-between">
                          <Text color={textColor}>{location.country}</Text>
                          <HStack>
                            <Text color="green.400">{location.views} views</Text>
                            <Text color="gray.400">({formatPercentage(location.percentage)})</Text>
                          </HStack>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                </VStack>
              )}
            </TabPanel>

            {/* Engagement Tab */}
            <TabPanel>
              {engagementAnalytics && (
                <VStack gap={6} align="stretch">
                  <SimpleGrid columns={{ base: 2, md: 4 }} gap={6}>
                    <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <StatLabel>Likes</StatLabel>
                      <StatNumber color="green.400">{formatNumber(engagementAnalytics.likes)}</StatNumber>
                    </Stat>

                    <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <StatLabel>Comments</StatLabel>
                      <StatNumber color="blue.400">{formatNumber(engagementAnalytics.comments)}</StatNumber>
                    </Stat>

                    <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <StatLabel>Shares</StatLabel>
                      <StatNumber color="purple.400">{formatNumber(engagementAnalytics.shares)}</StatNumber>
                    </Stat>

                    <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <StatLabel>Engagement Rate</StatLabel>
                      <StatNumber color="orange.400">{formatPercentage(engagementAnalytics.engagementRate)}</StatNumber>
                    </Stat>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 3 }} gap={6}>
                    <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <StatLabel>Bookmarks</StatLabel>
                      <StatNumber color="yellow.400">{formatNumber(engagementAnalytics.bookmarks)}</StatNumber>
                    </Stat>

                    <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <StatLabel>Subscriptions</StatLabel>
                      <StatNumber color="red.400">{formatNumber(engagementAnalytics.subscriptions)}</StatNumber>
                    </Stat>

                    <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <StatLabel>Retention Rate</StatLabel>
                      <StatNumber color="teal.400">{formatPercentage(engagementAnalytics.retentionRate)}</StatNumber>
                    </Stat>
                  </SimpleGrid>
                </VStack>
              )}
            </TabPanel>

            {/* Traffic Tab */}
            {!contentId && trafficAnalytics && (
              <TabPanel>
                <VStack gap={6} align="stretch">
                  <SimpleGrid columns={{ base: 2, md: 4 }} gap={6}>
                    <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <StatLabel>Total Views</StatLabel>
                      <StatNumber color="purple.400">{formatNumber(trafficAnalytics.totalViews)}</StatNumber>
                    </Stat>

                    <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <StatLabel>Unique Visitors</StatLabel>
                      <StatNumber color="blue.400">{formatNumber(trafficAnalytics.uniqueVisitors)}</StatNumber>
                    </Stat>

                    <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <StatLabel>Bounce Rate</StatLabel>
                      <StatNumber color="red.400">{formatPercentage(trafficAnalytics.bounceRate)}</StatNumber>
                    </Stat>

                    <Stat bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <StatLabel>Avg Session</StatLabel>
                      <StatNumber color="green.400">{Math.round(trafficAnalytics.averageSessionDuration / 60)}m</StatNumber>
                    </Stat>
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
                    <Box bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <Heading size="md" mb={4} color={textColor}>Top Content</Heading>
                      <VStack align="stretch" gap={3}>
                        {trafficAnalytics.topContent.slice(0, 10).map((content, index) => (
                          <HStack key={content.contentId} justify="space-between">
                            <VStack align="start" gap={0}>
                              <Text fontWeight="medium" color={textColor}>{content.title}</Text>
                              <Text fontSize="sm" color={descriptionColor}>{content.category}</Text>
                            </VStack>
                            <Text color="purple.400">{formatNumber(content.views)} views</Text>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>

                    <Box bg={cardBg} p={6} borderRadius="lg" border="1px" borderColor={borderColor}>
                      <Heading size="md" mb={4} color={textColor}>Traffic Sources</Heading>
                      <VStack align="stretch" gap={3}>
                        {trafficAnalytics.trafficSources.map((source, index) => (
                          <HStack key={index} justify="space-between">
                            <Text color={textColor} textTransform="capitalize">{source.source}</Text>
                            <HStack>
                              <Text color="blue.400">{formatNumber(source.views)}</Text>
                              <Text color="gray.400">({formatPercentage(source.percentage)})</Text>
                            </HStack>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  </SimpleGrid>
                </VStack>
              </TabPanel>
            )}
          </TabPanels>
        </Tabs>
      </VStack>
    </Container>
  );
};

export default AnalyticsDashboard;
